import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import ScrollToTop from "@/components/ScrollToTop";
import { MallProvider, useMall } from "@/context/MallContext";
import { CartProvider } from "@/context/CartContext";
import { SettingsProvider } from "@/context/SettingsContext";

import Home from "@/pages/Home";
import Product from "@/pages/Product";
import SiteEntry, { SiteExit } from "@/pages/SiteEntry";
import Shirts from "@/pages/Shirts";
import DtfPage from "@/pages/DtfPage";
import ShirtProduct from "@/pages/ShirtProduct";
import GangSheet from "@/pages/GangSheet";
import Signs from "@/pages/Signs";
import SignCategory from "@/pages/SignCategory";
import SignProduct from "@/pages/SignProduct";
import OrderStudio from "@/pages/OrderStudio";
import Checkout from "@/pages/Checkout";
import Bags from "@/pages/Bags";
import OrderConfirmation from "@/pages/OrderConfirmation";
import TrackOrder from "@/pages/TrackOrder";
import ArtworkRequest from "@/pages/ArtworkRequest";
import Wholesale from "@/pages/Wholesale";
import Classes from "@/pages/Classes";
import WebsiteDesign from "@/pages/WebsiteDesign";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Account from "@/pages/Account";
import Purchases from "@/pages/Purchases";
import Payouts from "@/pages/Payouts";
import GangSheets from "@/pages/GangSheets";
import ServiceRequestsPage from "@/pages/ServiceRequestsPage";
import EmailGuidePage from "@/pages/EmailGuidePage";
import Admin from "@/pages/Admin";
import Proof from "@/pages/Proof";
import AuthCallback from "@/pages/AuthCallback";
import Marketplace from "@/pages/Marketplace";
import Listing from "@/pages/Listing";
import SellListing from "@/pages/SellListing";
import MyListings from "@/pages/MyListings";
import MachineServices from "@/pages/MachineServices";
import StorePage from "@/pages/StorePage";
import StorePolicies from "@/pages/StorePolicies";
import OpenSpace from "@/pages/OpenSpace";
import VendorOrders from "@/pages/VendorOrders";
import StoreProducts from "@/pages/StoreProducts";
import Promotions from "@/pages/Promotions";
import Analytics from "@/pages/Analytics";
import DropCards from "@/pages/DropCards";
import Reviews from "@/pages/Reviews";
import PayoutHistory from "@/pages/PayoutHistory";
import StoreBillboards from "@/pages/StoreBillboards";
import StoreInbox from "@/pages/StoreInbox";
import StoreDomain from "@/pages/StoreDomain";
import ConsignmentMode from "@/pages/ConsignmentMode";
import StoreClasses from "@/pages/StoreClasses";
import FoodCourt from "@/pages/FoodCourt";
import Spaces from "@/pages/Spaces";
import VendorTicket from "@/pages/VendorTicket";
import VendorSetup from "@/pages/VendorSetup";
import StoreHomepage from "@/pages/StoreHomepage";
import PaymentResult from "@/pages/PaymentResult";

function Protected({ children, admin }) {
    const { user, ready } = useAuth();
    if (!ready) return <div className="flex min-h-screen items-center justify-center bg-brand-ink text-zinc-400">Loading…</div>;
    if (!user) return <Navigate to="/login" replace />;
    if (admin && user.role !== "admin") return <Navigate to="/account" replace />;
    return children;
}

function MallRedirect() {
    const { slug } = useParams();
    return <Navigate to={`/stores/${slug}`} replace />;
}

/** The old racks and shelves all live under Shirts now. */
const OLD_SHELF = { "same-day": "/shirts?ready=same_day", standard: "/shirts?ready=weeks_2_4" };
function ShelfRedirect() {
    const { bucket } = useParams();
    return <Navigate to={OLD_SHELF[bucket] || "/shirts"} replace />;
}
function ShirtKindRedirect() {
    const { kind } = useParams();
    return <Navigate to={kind === "dtf" ? "/shirts/dtf" : "/shirts"} replace />;
}

/**
 * 13thav.com's front door. A visitor gets the pitch; a Virtual Brick & Mortar tenant and
 * anybody who clicked "See it live" get the white-label store site instead.
 */
/**
 * `/` is the white-label pitch page — that was the user's call. The only visitors who get a shop
 * at the root are the ones standing on that shop's own hostname, and the demo, which borrows the
 * root on purpose so the buyer sees a real shop with the red bar over it.
 */
function FrontDoor() {
    const { demo, soloByHost } = useMall();
    return (demo || soloByHost) ? <Home /> : <Product />;
}

function AppRoutes() {
    const location = useLocation();
    // Google OAuth returns to {origin}/account#session_id=... — handle it before any route renders.
    if (location.hash?.includes("session_id=")) return <AuthCallback />;
    return (
                        <Routes>
                            <Route path="/" element={<FrontDoor />} />
                            <Route path="/whitelabel" element={<Product />} />
                            <Route path="/site/exit" element={<SiteExit />} />
                            <Route path="/site/:slug" element={<SiteEntry />} />
                            <Route path="/shop/:bucket" element={<ShelfRedirect />} />
                            <Route path="/shirts" element={<Shirts />} />
                            <Route path="/shirts/dtf" element={<DtfPage />} />
                            <Route path="/shirts/dtf/gang-sheet" element={<GangSheet />} />
                            <Route path="/shirts/:kind" element={<ShirtKindRedirect />} />
                            <Route path="/shirts/:kind/:slug" element={<ShirtProduct />} />
                            <Route path="/signs" element={<Signs />} />
                            <Route path="/signs/:category" element={<SignCategory />} />
                            <Route path="/signs/:category/:slug" element={<SignProduct />} />
                            <Route path="/order/white-tees" element={<Navigate to="/shirts/white" replace />} />
                            <Route path="/order/colored-apparel" element={<Navigate to="/shirts/colored" replace />} />
                            <Route path="/order/dtf-gang-sheet" element={<Navigate to="/shirts/dtf/gang-sheet" replace />} />
                            <Route path="/order/custom-sign" element={<Navigate to="/signs" replace />} />
                            <Route path="/order/:slug" element={<OrderStudio />} />
                            <Route path="/cart" element={<Bags />} />
                            <Route path="/checkout" element={<Navigate to="/cart" replace />} />
                            <Route path="/checkout/:store" element={<Checkout />} />
                            <Route path="/order-confirmation/:id" element={<OrderConfirmation />} />
                            <Route path="/track" element={<TrackOrder />} />
                            <Route path="/proof/:token" element={<Proof />} />
                            <Route path="/artwork-request" element={<ArtworkRequest />} />
                            <Route path="/wholesale" element={<Wholesale />} />
                            <Route path="/classes" element={<Classes />} />
                            <Route path="/website-design" element={<WebsiteDesign />} />
                            <Route path="/stores" element={<Navigate to="/directory" replace />} />
                            <Route path="/stores/:slug" element={<StorePage />} />
                            <Route path="/stores/:slug/policies" element={<StorePolicies />} />
                            <Route path="/account/sell" element={<Protected><OpenSpace /></Protected>} />
                            <Route path="/account/orders" element={<Protected><VendorOrders /></Protected>} />
                            <Route path="/account/products" element={<Protected><StoreProducts /></Protected>} />
                            <Route path="/account/billboards" element={<Protected><StoreBillboards /></Protected>} />
                            <Route path="/account/coupons" element={<Protected><Promotions /></Protected>} />
                            <Route path="/account/consignment" element={<Protected><ConsignmentMode /></Protected>} />
                            <Route path="/account/wizard" element={<Protected><Account /></Protected>} />
                            <Route path="/account/classes" element={<Protected><StoreClasses /></Protected>} />
                            <Route path="/community" element={<FoodCourt />} />
                            <Route path="/food-court" element={<Navigate to="/community" replace />} />
                            <Route path="/spaces" element={<Spaces />} />
                            <Route path="/account/inbox" element={<Protected><StoreInbox /></Protected>} />
                            <Route path="/account/orders/:id/ticket" element={<Protected><VendorTicket /></Protected>} />
                            <Route path="/account/store" element={<Protected><VendorSetup /></Protected>} />
                            <Route path="/account/homepage" element={<Protected><StoreHomepage /></Protected>} />
                            <Route path="/account/domain" element={<Protected><StoreDomain /></Protected>} />
                            <Route path="/directory" element={<Home />} />
                            <Route path="/mall" element={<Navigate to="/directory" replace />} />
                            <Route path="/mall/setup" element={<Navigate to="/account/store" replace />} />
                            <Route path="/mall/:slug" element={<MallRedirect />} />
                            <Route path="/payment/success" element={<PaymentResult />} />
                            <Route path="/payment/cancel" element={<PaymentResult cancelled />} />
                            <Route path="/login" element={<Login />} />
                            <Route path="/register" element={<Register />} />
                            <Route path="/supplies" element={<Marketplace />} />
                            <Route path="/supplies/sell" element={<Protected><SellListing /></Protected>} />
                            <Route path="/supplies/:slug" element={<Listing />} />
                            <Route path="/services" element={<MachineServices />} />
                            <Route path="/account/listings" element={<Protected><MyListings /></Protected>} />
                            <Route path="/account" element={<Protected><Account /></Protected>} />
                            <Route path="/account/purchases" element={<Protected><Purchases /></Protected>} />
                            <Route path="/account/payouts" element={<Protected><Payouts /></Protected>} />
                            <Route path="/account/gang-sheets" element={<Protected><GangSheets /></Protected>} />
                            <Route path="/account/service-requests" element={<Protected admin><ServiceRequestsPage /></Protected>} />
                            <Route path="/account/email-guide" element={<Protected><EmailGuidePage /></Protected>} />
                            <Route path="/account/discounts" element={<Protected><Promotions /></Protected>} />
                            <Route path="/account/analytics" element={<Protected><Analytics /></Protected>} />
                            <Route path="/account/drop-cards" element={<Protected><DropCards /></Protected>} />
                            <Route path="/account/reviews" element={<Protected><Reviews /></Protected>} />
                            <Route path="/account/payout-history" element={<Protected><PayoutHistory /></Protected>} />
                            <Route path="/admin" element={<Protected admin><Admin /></Protected>} />
                            <Route path="*" element={<Navigate to="/" replace />} />
                        </Routes>
    );
}

function App() {
    return (
        <AuthProvider>
            <SettingsProvider>
                <CartProvider>
                    <MallProvider>
                    <BrowserRouter>
                        <ScrollToTop />
                        <Toaster theme="dark" position="top-center" richColors />
                        <AppRoutes />
                    </BrowserRouter>
                    </MallProvider>
                </CartProvider>
            </SettingsProvider>
        </AuthProvider>
    );
}

export default App;
